import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "xpro_onboarding_v1";

const defaultState = {
  completedSteps: [],
  featureUsage: {},
  lastUpdatedAt: null,
  seenTips: {},
};

const OnboardingContext = createContext({
  state: defaultState,
  markStepCompleted: () => {},
  registerFeatureUse: () => {},
  markTipSeen: () => {},
  hasSeenTip: () => false,
});

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return defaultState;
    return {
      ...defaultState,
      ...parsed,
      completedSteps: Array.isArray(parsed.completedSteps)
        ? parsed.completedSteps
        : [],
      featureUsage:
        parsed.featureUsage && typeof parsed.featureUsage === "object"
          ? parsed.featureUsage
          : {},
      seenTips:
        parsed.seenTips && typeof parsed.seenTips === "object"
          ? parsed.seenTips
          : {},
    };
  } catch {
    return defaultState;
  }
}

function saveToStorage(state) {
  try {
    const payload = {
      ...state,
      lastUpdatedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // falha silenciosa
  }
}

export function OnboardingProvider({ children }) {
  const [state, setState] = useState(() => loadFromStorage());

  useEffect(() => {
    saveToStorage(state);
  }, [state]);

  const markStepCompleted = useCallback((stepId) => {
    if (!stepId) return;
    setState((prev) => {
      if (prev.completedSteps.includes(stepId)) return prev;
      return {
        ...prev,
        completedSteps: [...prev.completedSteps, stepId],
      };
    });
  }, []);

  const registerFeatureUse = useCallback((featureKey) => {
    if (!featureKey) return;
    const nowIso = new Date().toISOString();
    setState((prev) => {
      const prevStats = prev.featureUsage?.[featureKey] || {
        count: 0,
        lastUsedAt: null,
      };
      return {
        ...prev,
        featureUsage: {
          ...prev.featureUsage,
          [featureKey]: {
            count: (prevStats.count || 0) + 1,
            lastUsedAt: nowIso,
          },
        },
      };
    });
  }, []);

  const markTipSeen = useCallback((tipId) => {
    if (!tipId) return;
    setState((prev) => ({
      ...prev,
      seenTips: {
        ...prev.seenTips,
        [tipId]: true,
      },
    }));
  }, []);

  const hasSeenTip = useCallback(
    (tipId) => {
      if (!tipId) return false;
      return !!state.seenTips?.[tipId];
    },
    [state.seenTips]
  );

  const value = useMemo(
    () => ({
      state,
      markStepCompleted,
      registerFeatureUse,
      markTipSeen,
      hasSeenTip,
    }),
    [state, markStepCompleted, registerFeatureUse, markTipSeen, hasSeenTip]
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  return useContext(OnboardingContext);
}

